import { Component, computed, inject } from '@angular/core';

import { MDTagComponent } from '@melodic-ui/components';
import { ImpersonationContextService } from '../../impersonation/impersonation-context';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
	selector: 'lb-access-denied-403',
	standalone: true,
	imports: [MDTagComponent],
	templateUrl: './access-denied-403.component.html',
	styleUrl: './access-denied-403.component.scss'
})
export class AccessDenied403Component {
	private readonly _impersonation = inject(ImpersonationContextService);
	private readonly _authService = inject(AuthenticationService);

	/**
	 * The person being acted as, when that is what got us refused.
	 *
	 * An impersonation is per tab, not per app, and in production all three apps share one origin. So
	 * walking this tab from the learner app into the instructor app carries it along, the guard
	 * refuses a page the impersonated person may not see, and the denial says nothing about why —
	 * leaving Go Back, which returns to the same refusal, and a link off the product.
	 */
	public readonly actingAsUserID = this._impersonation.targetUserID;
	public readonly isImpersonating = computed(() => this.actingAsUserID() !== null);

	/**
	 * Gives up the impersonation and reloads this app as ourselves.
	 *
	 * The cached profile goes with it: AuthenticationService.init() returns that early and never
	 * refetches, so dropping only the context would bring the app back up as the person we just
	 * stopped being — still refused, and now with nothing left to explain it.
	 *
	 * Not AccountStateService.stopImpersonation, which is the deliberate exit and sends you to the
	 * admin app. This is the recovery: you were trying to use THIS app, so it reloads here.
	 */
	public endImpersonation(): void {
		this._impersonation.clear();
		this._authService.clearUser();

		// baseURI, not '/': the apps are one origin under different paths, so '/' would drop an
		// instructor out of the instructor app on the way to recovering.
		this.navigate(document.baseURI);
	}

	/**
	 * The one line of navigation, kept separate because jsdom refuses to let a test stub
	 * window.location — so this is the seam the spec overrides to pin where recovery goes.
	 *
	 * replace(), not assign(): the denial should not be sitting in history behind us.
	 */
	protected navigate(url: string): void {
		window.location.replace(url);
	}
}
